import { BUILDER_END, builderLinkRefusal, impliedLines, isAside, jobLinkRefusal, parseNeed, phasesOf, titleCase, walkOrder, type BuilderTree, type ProcessStepDto } from '@nsb/core';
import type { ChoiceStep } from '../tree/choosing.ts';
import { boardWaits, lineId } from '../tree/layout.ts';
import { masterTiles, onBoard } from '../tree/master.ts';
import { linesOf, type TileLine, type TilePhase, type TileStep } from '../tree/model.ts';
import type { ProcessModel } from './useProcess.ts';

/**
 * What a step needs and what it opens, one step at a time (design study 31, slice 5; §5, A), as the master's card and a
 * job's setup panel list them (NeedsOpens): each line a row with Take out; a line a longer way already gives marked so,
 * with the way (core `impliedLines`, §0.1), and "Not drawn on the board." where the board's drawing leaves it out; and
 * the step Next step in the tree goes to (core `walkOrder`). Pure: the two pages feed it their own steps.
 */

/** Which list: what the step needs (it comes after), or what it opens (comes after it). */
export type Side = 'needs' | 'opens';

export interface NeedsOpensRow {
  /** The step at the other end of the line, by its key on the page (a key of the tree on the master, a step's id on a job). */
  key: string;
  /** Its name as shown. */
  name: string;
  /** The decision's option the line leaves by, in its words (the master's): none for a line from the whole of a step. */
  option: string | null;
  /** A longer way already gives the line: the steps between by name, from what is needed to what needs it; else null. */
  through: string[] | null;
  /** For a line a longer way gives, read from the board: true when its drawing leaves the line out; unsaid without one. */
  hidden?: boolean;
  /** On a job, the step waited on is a gate or an inspection not passed: the line is taken out only with a reason. */
  holds?: boolean;
  /** Not taken out here, and why in a few words (a gate after a gate: "the gates’ order"). */
  fixed?: string | null;
}

export interface NeedsOpensRows {
  needs: NeedsOpensRow[];
  opens: NeedsOpensRow[];
}

/**
 * The rows of a step's two lists. `needs` is each step's needs as stored, a key or a decision's key and one of its
 * options (`key:option`), over the steps the page reads; `name` names a step by its key; `option` names an option;
 * `hidden` says whether the board leaves a line a longer way gives out of its drawing (`step` waiting on `need`).
 * A line from an option is never marked: the board always draws it.
 */
export function needsOpensRows(
  needs: ReadonlyMap<string, readonly string[]>,
  key: string,
  name: (k: string) => string,
  opts: { option?: (decision: string, option: string) => string | null; hidden?: (step: string, need: string) => boolean } = {},
): NeedsOpensRows {
  const implied = impliedLines(needs);
  const row = (other: string, step: string, entry: string): NeedsOpensRow => {
    const { key: decision, option } = parseNeed(entry);
    const through = implied.get(step)?.find((l) => l.need === entry)?.through ?? null;
    const hidden = through && opts.hidden ? opts.hidden(step, entry) : undefined;
    return {
      key: other,
      name: name(other),
      option: option === null ? null : (opts.option?.(decision, option) ?? null),
      through: through ? through.map(name) : null,
      ...(hidden === undefined ? {} : { hidden }),
    };
  };
  const seen = new Set([key]);
  const mine: NeedsOpensRow[] = [];
  for (const entry of needs.get(key) ?? []) {
    const k = parseNeed(entry).key;
    if (seen.has(k)) continue;
    seen.add(k);
    mine.push(row(k, key, entry));
  }
  const opens: NeedsOpensRow[] = [];
  for (const [k, ns] of needs) {
    if (k === key) continue;
    const entry = ns.find((d) => parseNeed(d).key === key);
    if (entry !== undefined) opens.push(row(k, k, entry));
  }
  return { needs: mine, opens };
}

/** Names as a list in words: "A", "A and B", "A, B and C". */
const andWords = (names: readonly string[]): string => (names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names.at(-1)!}` : (names[0] ?? ''));

/** How a line a longer way gives opens its words, on each list: the step is already after what it needs; what it opens is already after it. */
export const IMPLIED_HEAD: Record<Side, string> = { needs: 'Already after it', opens: 'Already after this step' };

/** The words under a line a longer way gives (study 31, §5 A): "Already after it, through A and B. Not drawn on the board." */
export const impliedWords = (side: Side, through: readonly string[], hidden?: boolean): string =>
  `${IMPLIED_HEAD[side]}, through ${andWords(through)}.${hidden ? ' Not drawn on the board.' : ''}`;

/** What the board draws, as a step's card reads it: its steps and lines, and the lines it would draw of any given. */
export interface CardBoard {
  steps: readonly TileStep[];
  lines: readonly TileLine[];
  drawn: (lines: readonly TileLine[]) => readonly TileLine[];
}

/**
 * Whether the board leaves the line `dst` ← `src` (by key in the drawing) out of its drawing: it would draw just what
 * it draws now without it. So taking it out draws nothing new, the longer way still drawn (the study's §0.1).
 */
function leftOff(board: Pick<CardBoard, 'lines' | 'drawn'>): (dst: string, src: string) => boolean {
  const ids = (ls: readonly TileLine[]) => [...new Set(ls.map(lineId))].sort().join('\n');
  const now = ids(board.drawn(board.lines));
  return (dst, src) => now === ids(board.drawn(board.lines.filter((l) => !(l.dst === dst && l.src === src))));
}

/** A page's board, as a card reads it: its tiles and lines, drawn as the page draws them (every phase drawn, as both pages do). */
export const cardBoard = (phases: readonly TilePhase[], steps: readonly TileStep[], lines: readonly TileLine[]): CardBoard => ({ steps, lines, drawn: boardWaits(phases, steps, true) });

/** The master's board, as a card reads it (TreeBuilder's tiles and lines). */
export function masterBoard(t: BuilderTree): CardBoard {
  const tiles = masterTiles(t, new Map(), () => null);
  return cardBoard(tiles.phases, tiles.steps, linesOf(tiles.steps));
}

/** A step's lists on the master's card: every node's lines, options by their words, read from the board when given. */
export function masterNeedsOpens(t: BuilderTree, key: string, board?: CardBoard): NeedsOpensRows {
  const byKey = new Map(t.nodes.map((n) => [n.key, n]));
  const drawnKeys = new Set(board?.steps.map((s) => s.key));
  const off = board ? leftOff(board) : null;
  return needsOpensRows(
    new Map(t.nodes.map((n) => [n.key, n.dependsOn])),
    key,
    (k) => titleCase(byKey.get(k)?.title ?? k),
    {
      option: (d, o) => byKey.get(d)?.options.find((x) => x.id === o)?.text ?? null,
      // A line from or to a step not on the board (a task in a step's list) is not drawn on it.
      hidden: off ? (step, need) => !drawnKeys.has(step) || !drawnKeys.has(need) || off(step, need) : undefined,
    },
  );
}

/** The master's walk for Next step in the tree: the steps on the board, phase by phase, each phase in the order of its waits. */
export function masterWalk(t: BuilderTree): string[] {
  const steps = onBoard(t);
  return walkOrder(
    steps.map((n) => ({ key: n.key, phase: n.phase, needs: n.dependsOn, ...(n.kind === 'start' ? { end: 'start' as const } : n.kind === 'end' ? { end: 'end' as const } : {}) })),
    steps.map((n) => n.key),
    t.phases.map((p) => p.key),
  );
}

/** The two ends of a line from `key`'s list `side`: what it needs, it waits on; what it opens waits on it. As [waits, waited on]. */
const endsOf = (side: Side, key: string, other: string): [string, string] => (side === 'needs' ? [key, other] : [other, key]);

/**
 * Why `other` cannot go into `key`'s list `side` on the master, in words, or null when it can (choosing, slice 5): what
 * core `builderLinkRefusal` refuses (the ends, a task of its own, a loop, a wait on a step in a later phase), or the
 * line there already. A line from a decision's option can be made the whole step's line, as drawing it again does.
 */
export function masterChoiceRefusal(t: Pick<BuilderTree, 'nodes' | 'phases'>, key: string, side: Side, other: string): string | null {
  const [from, to] = endsOf(side, key, other);
  const why = builderLinkRefusal(t, from, to);
  if (why) return why;
  const a = t.nodes.find((n) => n.key === from)!;
  const b = t.nodes.find((n) => n.key === to)!;
  return a.dependsOn.includes(to) ? `“${titleCase(a.title)}” already comes after “${titleCase(b.title)}”.` : null;
}

/** What the master's typed field finds while choosing: the board's steps in the tree's walk, then the tasks in a step's list, named where they are. */
export function masterChoices(t: BuilderTree): ChoiceStep[] {
  const byKey = new Map(t.nodes.map((n) => [n.key, n]));
  const walk = masterWalk(t);
  const walked = new Set(walk);
  const rest = t.nodes.filter((n) => !walked.has(n.key) && !isAside(n));
  return [
    ...walk.map((k) => ({ key: k, name: titleCase(byKey.get(k)!.title) })),
    ...rest.map((n) => {
      const up = n.parent ? byKey.get(n.parent) : undefined;
      return { key: n.key, name: titleCase(n.title), ...(up ? { where: `In ${titleCase(up.title)}’s list` } : {}) };
    }),
  ];
}

/** The step after `key` in a walk: none after the last, or for a step not in it (a task in a step's list). */
export function nextInWalk(walk: readonly string[], key: string): string | null {
  const at = walk.indexOf(key);
  return at >= 0 && at + 1 < walk.length ? walk[at + 1]! : null;
}

/**
 * The job's board beside a step's panel on the setup page (JobTree's): its phases, tiles and lines, what the panel reads
 * to say a line is not drawn on it, another step opened from the panel (a line's step, or Next step in the tree), and
 * "+ It needs…" or "+ It opens…" choosing on the board (TreeView's `choosing`); without it, nothing is added there.
 */
export interface PanelBoard {
  phases: readonly TilePhase[];
  steps: readonly TileStep[];
  lines: readonly TileLine[];
  onOpen: (stepId: string) => void;
  onChoose?: (stepId: string, side: Side) => void;
}

/** A gate after a gate is the gates' order: it is not taken out from either one's panel (as the panel's waits had it). */
export const GATES_ORDER = 'the gates’ order';

/** A step of a job by its name, as its lists name it: a copy by its own name too. */
export const jobName = (s: Pick<ProcessStepDto, 'title' | 'instance'>): string => `${titleCase(s.title)}${s.instance && s.instance !== s.title ? ` (${s.instance})` : ''}`;

/**
 * A step's lists on a job's setup panel: its waits and what waits on it, from the job's links between the steps on it.
 * A wait on a gate or an inspection not passed is taken out only with a reason, from either list (taking out what a gate
 * opens is taking out a wait on that gate); a gate after a gate keeps the gates' order. Given the board as the setup page
 * draws it (JobTree's tiles and lines), a line a longer way gives says whether the drawing leaves it out, read where
 * each end is drawn: a task in its step's tile, a task done early in its own.
 */
export function jobNeedsOpens(model: Pick<ProcessModel, 'dto'>, s: ProcessStepDto, board?: CardBoard): NeedsOpensRows {
  const live = model.dto.steps.filter((x) => !x.off);
  const byId = new Map(live.map((x) => [x.id, x]));
  const needs = new Map<string, string[]>(live.map((x) => [x.id, []]));
  for (const l of model.dto.links) if (byId.has(l.stepId) && byId.has(l.needsId) && l.stepId !== l.needsId) needs.get(l.stepId)!.push(l.needsId);
  const drawnKeys = new Set(board?.steps.map((t) => t.key));
  /** The tile a step is drawn in: itself, or the step it is in. */
  const tileOf = (id: string): string | null => {
    let x = byId.get(id);
    for (let i = 0; x && !drawnKeys.has(x.id) && x.parentId && i < 100; i++) x = byId.get(x.parentId);
    return x && drawnKeys.has(x.id) ? x.id : null;
  };
  const off = board ? leftOff(board) : null;
  const rows = needsOpensRows(needs, s.id, (id) => (byId.get(id) ? jobName(byId.get(id)!) : id), {
    hidden: off
      ? (step, need) => {
          const a = tileOf(step);
          const b = tileOf(need);
          return !a || !b || a === b || off(a, b);
        }
      : undefined,
  });
  const holding = (x: ProcessStepDto) => (x.kind === 'gate' || x.kind === 'inspection') && x.state !== 'done';
  const gates = (a: ProcessStepDto, b: ProcessStepDto) => a.kind === 'gate' && b.kind === 'gate';
  return {
    needs: rows.needs.map((r) => {
      const w = byId.get(r.key)!;
      return { ...r, holds: holding(w), fixed: gates(s, w) ? GATES_ORDER : null };
    }),
    opens: rows.opens.map((r) => {
      const x = byId.get(r.key)!;
      return { ...r, holds: holding(s), fixed: gates(x, s) ? GATES_ORDER : null };
    }),
  };
}

/** A job's walk for Next step in the tree: its steps at the top, by the phase each sits in, then the order of their waits and the job's. */
export function jobWalk(model: Pick<ProcessModel, 'dto' | 'phases'>): string[] {
  const live = model.dto.steps.filter((x) => !x.off);
  const ids = new Set(live.map((x) => x.id));
  const tops = live.filter((x) => !x.parentId || !ids.has(x.parentId));
  const start = model.dto.tree.start ?? null;
  return walkOrder(
    tops.map((x) => ({
      key: x.id,
      phase: model.phases.get(x.id) ?? x.phase,
      needs: model.dto.links.filter((l) => l.stepId === x.id).map((l) => l.needsId),
      ...(x.key !== null && x.key === start ? { end: 'start' as const } : x.key === BUILDER_END ? { end: 'end' as const } : {}),
    })),
    tops.map((x) => x.id),
    phasesOf(model.dto.tree).map((p) => p.key),
  );
}

/**
 * Why `other` cannot go into the list `side` of the job's step `stepId`, in words, or null when it can (choosing, slice
 * 5): a step left out of the job (a dot) or not on it, the wait there already, or what core `jobLinkRefusal` refuses
 * (the start, the end, itself, one inside the other, a loop), read with the job's phases.
 */
export function jobChoiceRefusal(model: Pick<ProcessModel, 'dto' | 'tree' | 'process' | 'phases'>, stepId: string, side: Side, other: string): string | null {
  if (other.startsWith('out:')) return `${titleCase(model.tree.node(other.slice(4))?.title ?? 'That step')} is left out of this job: put it back first.`;
  const live = (id: string) => model.dto.steps.find((x) => x.id === id && !x.off);
  if (!live(other)) return 'That step is not on this job.';
  const [from, to] = endsOf(side, stepId, other);
  if (model.dto.links.some((l) => l.stepId === from && l.needsId === to)) return `${jobName(live(from)!)} already waits on ${jobName(live(to)!)}.`;
  return jobLinkRefusal(model.process, from, to, model.phases);
}

/** What a job's typed field finds while choosing: its steps in the job's walk, then the tasks in a step's list, named where they are. */
export function jobChoices(model: Pick<ProcessModel, 'dto' | 'phases'>): ChoiceStep[] {
  const live = model.dto.steps.filter((x) => !x.off);
  const byId = new Map(live.map((x) => [x.id, x]));
  const walk = jobWalk(model);
  const walked = new Set(walk);
  return [
    ...walk.map((id) => ({ key: id, name: jobName(byId.get(id)!) })),
    ...live
      .filter((x) => !walked.has(x.id))
      .map((x) => {
        const up = x.parentId ? byId.get(x.parentId) : undefined;
        return { key: x.id, name: jobName(x), ...(up ? { where: `In ${jobName(up)}’s list` } : {}) };
      }),
  ];
}
